import { Injectable } from "@angular/core";
import { LaunchpadAsset } from "@modules/launchpad/domain/entities/launchpad-asset";
import { Observable } from "rxjs";
import { filter, map } from "rxjs/operators";
import { LaunchpadRemote } from "@modules/launchpad/domain/infrastructure/launchpad.remote";
import { LaunchpadState, LaunchpadStateService } from "@modules/launchpad/domain/+state/launchpad.state.service";
import { LaunchpadNewsletterSub } from "@modules/launchpad/domain/entities/launchpad-newsletter-sub";
import { Router } from "@angular/router";
import { UserService } from "@main-services/user.service";
import { LaunchpadTrackingFacade } from "@modules/launchpad/domain/+state/launchpad-tracking.facade";

@Injectable({
	providedIn: "root"
})
export class LaunchpadFacade {
	constructor(
		private readonly launchpadRemote: LaunchpadRemote,
		private readonly state: LaunchpadStateService,
		private readonly router: Router,
		private readonly userService: UserService,
		private readonly launchpadTrackingFacade: LaunchpadTrackingFacade
	) {}

	public getState$(): Observable<LaunchpadState> {
		// filter out "empty" state
		return this.state.getState$().pipe(filter((state) => !!state && !!state.assets));
	}

	public getLiveAssets$(): Observable<LaunchpadAsset[]> {
		return this.getState$().pipe(map((state) => state.assets.filter((a) => a.is_live)));
	}

	public getAssetsRemote$(): Observable<LaunchpadAsset[]> {
		return this.launchpadRemote.getLaunchpadAssets$();
	}

	public getNewsletterSub$(): Observable<LaunchpadNewsletterSub> {
		return this.launchpadRemote.getNewsletterSub$();
	}

	private setNewsletterSub(): void {
		this.launchpadRemote.setNewsletterSub$().subscribe(() => {
			this.state.updateNewsletterSub(true);
			this.launchpadTrackingFacade.trackNewsletterSubscribe(true);
		});
	}

	private unsetNewsletterSub(): void {
		this.launchpadRemote.unsetNewsletterSub$().subscribe(() => {
			this.state.updateNewsletterSub(false);
			this.launchpadTrackingFacade.trackNewsletterSubscribe(false);
		});
	}

	public toggleAssetSub(asset: LaunchpadAsset): void {
		asset.is_subscribed ? this.unsetAssetSub(asset) : this.setAssetSub(asset);
	}

	public toggleNewsletterSub(isSubscribed: boolean): void {
		isSubscribed ? this.unsetNewsletterSub() : this.setNewsletterSub();
	}

	private setAssetSub(asset: LaunchpadAsset): void {
		if (!this.userService.user.isVerified()) {
			this.router.navigate([{ outlets: { modal: ["spotlight-verify"] } }], { skipLocationChange: true });
			return;
		}

		this.launchpadRemote.setAssetSub$(asset.id).subscribe(() => {
			this.state.updatAssetSub(asset.id, true);
			this.launchpadTrackingFacade.trackAssetSubscribe(asset.symbol, true);
		});
	}

	private unsetAssetSub(asset: LaunchpadAsset): void {
		this.launchpadRemote.unsetAssetSub$(asset.id).subscribe(() => {
			this.state.updatAssetSub(asset.id, false);
			this.launchpadTrackingFacade.trackAssetSubscribe(asset.symbol, false);
		});
	}
}
